import { Injectable, WritableSignal, signal } from "@angular/core";
import { v4 as uuid } from "uuid";
import { compile, safeParse } from "@hsgovtech/hsutils";
import { Directory } from "@capacitor/filesystem";
import { get } from "lodash-es";
import { Client } from "../types/Client/Client";
import { RequestService } from "./request.service";
import { ClientSettings } from "../types/Client/ClientSettings";
import { DownloadedClientRepository } from "../repositories/downloaded.client.repository";
import { AvailableClientRepository } from "../repositories/available.client.repository";
import { EnvironmentService } from "./environment.service";
import { DevModeService } from "./dev.mode.service";
import { SyncHistoryItem } from "../types/SyncHistory/SyncHistoryItem";
import { LogService } from "./log.service";
import { DownloadedClient } from "../types/Client/DownloadedClient";
import { BaseService } from "./base.service";
import {
  convertStringsToBooleans,
  mergeArray,
  safeJSONParse,
} from "../../utils";
import { AuthCredentials } from "../types/Auth/Auth";
import { InspectionType } from "../types/Inspection/InspectionType";
import { Tab } from "../types/Tab/Tab";
import { Printable } from "../types/Printable/Printable";
import { ObjectURLService } from "./object.url.service";
import { DateService } from "./date.service";
import { FileService } from "./file.service";
import { ClientImagesRepository } from "../repositories/client.images.repository";
import { ClientConfigRepository } from "../repositories/client.config.repository";
import { ClientSchemasRepository } from "../repositories/client.schemas.repository";

@Injectable({
  providedIn: "root",
})
export class ClientService extends BaseService {
  currentClient = signal<any>(null);

  availableClients: Client[] = [];

  private hydratedSchemasCache = new Map<string, Record<string, any>>();

  private hydratedImagesCache = new Map<string, any[]>();

  private hydratedConfigCache = new Map<string, ClientSettings | undefined>();

  constructor(
    private availableClientRepo: AvailableClientRepository,
    private downloadedClientRepo: DownloadedClientRepository,
    private clientImagesRepo: ClientImagesRepository,
    private clientConfigRepo: ClientConfigRepository,
    private clientSchemasRepo: ClientSchemasRepository,
    private environmentService: EnvironmentService,
    private objectURLService: ObjectURLService,
    private devModeService: DevModeService,
    private requestService: RequestService,
    private fileService: FileService,
    private dateService: DateService,
    logService: LogService,
  ) {
    super(logService, "ClientService");
    this.downloadedClientRepo
      .getCurrentClient()
      .then((client) => {
        if (!client) {
          return;
        }
        this.hydrateClient(client)
          .then((hydrated) => {
            if (!hydrated) {
              return;
            }
            this.currentClient.set(hydrated);
            this.setDefaultDateFormat(hydrated);
          })
          .catch((err) => this.logger.error(err));
      })
      .catch((err) => this.logger.error(err));
  }

  private async persistClientImages(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    if (!client?.clientData) {
      return client;
    }
    const { images = [] } = client.clientData;
    if (!images.length) {
      return client;
    }
    const cachedImages = this.hydratedImagesCache.get(client.dlClientID);
    const imagesAlreadyPersisted = cachedImages === images;
    if (!imagesAlreadyPersisted) {
      await this.clientImagesRepo.setClientImages(client.dlClientID, images);
      this.hydratedImagesCache.set(client.dlClientID, images);
    }
    return {
      ...client,
      clientData: {
        ...client.clientData,
        images: [],
      },
    };
  }

  private async persistClientConfig(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    if (!client?.clientData?.clientConfig) {
      return client;
    }
    const { clientConfig } = client.clientData;
    const cachedConfig = this.hydratedConfigCache.get(client.dlClientID);
    const configAlreadyPersisted = cachedConfig === clientConfig;
    if (!configAlreadyPersisted) {
      await this.clientConfigRepo.setClientConfig(
        client.dlClientID,
        clientConfig,
      );
      this.hydratedConfigCache.set(client.dlClientID, clientConfig);
    }
    return {
      ...client,
      clientData: {
        ...client.clientData,
        clientConfig: undefined as unknown as ClientSettings,
      },
    };
  }

  private async persistClientSchemas(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    if (!client?.clientData?.schemas) {
      return client;
    }
    const { schemas } = client.clientData;
    if (!Object.keys(schemas).length) {
      return client;
    }
    const cachedSchemas = this.hydratedSchemasCache.get(client.dlClientID);
    const schemasAlreadyPersisted = cachedSchemas === schemas;
    if (!schemasAlreadyPersisted) {
      await this.clientSchemasRepo.setClientSchemas(client.dlClientID, schemas);
      this.hydratedSchemasCache.set(client.dlClientID, schemas);
    }
    return {
      ...client,
      clientData: {
        ...client.clientData,
        schemas: {},
      },
    };
  }

  private async persistStaticClientData(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    let updated = client;
    updated = await this.persistClientImages(updated);
    updated = await this.persistClientConfig(updated);
    updated = await this.persistClientSchemas(updated);
    return updated;
  }

  private async hydrateClientImages(
    client: DownloadedClient | null,
  ): Promise<DownloadedClient | null> {
    if (!client?.clientData) {
      return client;
    }
    if (client.clientData.images?.length) {
      return client;
    }
    const images = await this.clientImagesRepo.getClientImages(
      client.dlClientID,
    );
    if (!images?.length) {
      return client;
    }
    this.hydratedImagesCache.set(client.dlClientID, images);
    return {
      ...client,
      clientData: {
        ...client.clientData,
        images,
      },
    };
  }

  private async hydrateClientConfig(
    client: DownloadedClient | null,
  ): Promise<DownloadedClient | null> {
    if (!client?.clientData) {
      return client;
    }
    if (
      client.clientData.clientConfig &&
      Object.keys(client.clientData.clientConfig).length
    ) {
      return client;
    }
    const clientConfig = await this.clientConfigRepo.getClientConfig(
      client.dlClientID,
    );
    if (!clientConfig) {
      return client;
    }
    this.hydratedConfigCache.set(client.dlClientID, clientConfig);
    return {
      ...client,
      clientData: {
        ...client.clientData,
        clientConfig,
      },
    };
  }

  private async hydrateClientSchemas(
    client: DownloadedClient | null,
  ): Promise<DownloadedClient | null> {
    if (!client?.clientData) {
      return client;
    }
    if (
      client.clientData.schemas &&
      Object.keys(client.clientData.schemas).length
    ) {
      return client;
    }
    const schemas = await this.clientSchemasRepo.getClientSchemas(
      client.dlClientID,
    );
    if (!schemas) {
      return client;
    }
    this.hydratedSchemasCache.set(client.dlClientID, schemas);
    return {
      ...client,
      clientData: {
        ...client.clientData,
        schemas,
      },
    };
  }

  private async hydrateClient(
    client: DownloadedClient | null,
  ): Promise<DownloadedClient | null> {
    if (!client) {
      return client;
    }
    const withImages = await this.hydrateClientImages(client);
    const withConfig = await this.hydrateClientConfig(withImages);
    return this.hydrateClientSchemas(withConfig);
  }

  private hydrateClientArray(
    clients: DownloadedClient[],
  ): Promise<DownloadedClient[]> {
    return Promise.all(
      clients.map(async (client) => {
        return (await this.hydrateClient(client)) as DownloadedClient;
      }),
    );
  }

  public setDefaultDateFormat(client: DownloadedClient) {
    if (client.clientData?.clientConfig?.dateFormat) {
      this.dateService.setDefaultDateFormat(
        client.clientData.clientConfig.dateFormat,
        "cf",
      );
    }
  }

  public getDownloadedClients(): Promise<DownloadedClient[]> {
    return this.downloadedClientRepo
      .get()
      .then((clients) => this.hydrateClientArray(clients));
  }

  public getDownloadedClientsByAuth(
    auth: AuthCredentials,
  ): Promise<DownloadedClient[]> {
    return this.downloadedClientRepo
      .getClientsByAuth(auth)
      .then((clients) => this.hydrateClientArray(clients));
  }

  public clearCurrentClient(): Promise<void> {
    this.currentClient.set(null);
    return this.downloadedClientRepo.setCurrentClientId(undefined);
  }

  public async hasCurrentClient(): Promise<boolean> {
    return !!(await this.downloadedClientRepo.getCurrentClientId());
  }

  public clearAvailableClients(): Promise<void> {
    return this.availableClientRepo.clear();
  }

  public async saveDownloadedClient(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    const clientToPersist = await this.persistStaticClientData(client);
    await this.downloadedClientRepo.saveClient({ ...clientToPersist });
    return (await this.hydrateClient(clientToPersist)) as DownloadedClient;
  }

  public filterDevClients(clients: Client[]): Client[] {
    if (this.devModeService.isDevMode()) {
      const { clients: devClients } = this.environmentService.getDevVariables();
      if (!devClients) {
        return clients;
      }
      const devClientsArray = devClients.split(",");
      return clients.filter((client) => devClientsArray.includes(client.nick));
    }
    return clients;
  }

  public async getAvailableClients(): Promise<Client[]> {
    const availableClients = await this.availableClientRepo.get();
    return this.filterDevClients(availableClients);
  }

  public setAvailableClients(clients: Client[]): Promise<Client[]> {
    this.availableClients = clients;
    return this.availableClientRepo.set(clients);
  }

  public async updateClient(
    auth: AuthCredentials,
    client: DownloadedClient,
    loadingMessage: WritableSignal<string>,
    fresh: boolean = false,
  ): Promise<DownloadedClient> {
    const freshClient = await this.downloadClient(
      auth,
      client,
      loadingMessage,
      fresh,
    );
    const currentClient = this.currentClient();
    await this.setCurrentClient({
      ...currentClient,
      clientData: {
        ...currentClient.clientData,
        syncHistory: {
          upload: {},
          download: {
            syncID: uuid(),
            dts: new Date().getTime(),
          },
        },
        clientConfig: freshClient.clientData?.clientConfig,
        schemas: {
          ...currentClient.clientData?.schemas,
          ...freshClient.clientData?.schemas,
        },
        images: mergeArray(
          currentClient.clientData?.images,
          freshClient.clientData?.images,
          "fileName",
        ),
      },
    });
    return this.currentClient();
  }

  public createUniqueHash(options: any): string {
    const stableStringify = (obj: any): any => {
      if (obj === null || typeof obj !== "object") {
        return JSON.stringify(obj);
      }

      if (Array.isArray(obj)) {
        return `[${obj.map(stableStringify).join(",")}]`;
      }

      const keys = Object.keys(obj).sort();
      const props = keys.map(
        (key: string) => `"${key}":${stableStringify(obj[key])}`,
      );
      return `{${props.join(",")}}`;
    };
    const keyObj = {
      formID: options.formID,
      requestArgs: options.requestArgs || {},
      out: options.out,
      sortCol: options.sortColumn || "",
      sortDir: options.sortDirection || "",
    };
    return stableStringify(keyObj);
  }

  public getOptionsConfig(field: any) {
    if (field?.options?.formID) {
      return field.options;
    }
    if (field?.optionsConfig?.isNewVersion) {
      return field.optionsConfig;
    }
    return field.options;
  }

  public mergeOptions(schema: any, optionsMap: any, accessors: string[]): any {
    const updatedRecords = schema?.records?.map((record: any) => {
      return accessors.reduce((acc: any, accessor: string) => {
        const accessorParts = accessor.split(".");
        const parent = accessorParts[0];
        const item = get(acc, accessor);
        if (item) {
          const updatedItem = item.map((field: any) => {
            const options = this.getOptionsConfig(field);
            if (options?.formID) {
              return {
                ...field,
                options: optionsMap[this.createUniqueHash(options)],
              };
            }
            return field;
          });
          return { ...acc, [parent]: { ...record[parent], form: updatedItem } };
        }
        return acc;
      }, record);
    });
    return { ...schema, records: updatedRecords || [] };
  }

  public filterUniqueOptions(schema: any, accessors: string[]): any[] {
    const toGet =
      schema?.records?.reduce((acc: any, record: any) => {
        const filteredFields = accessors
          .map((accessor: string) => {
            const item = get(record, accessor);
            return item
              ? item.filter((field: any) => {
                  return this.getOptionsConfig(field)?.formID;
                })
              : undefined;
          })
          .filter(Boolean);
        return [...acc, ...filteredFields.flat()];
      }, [] as any[]) || [];
    const uniqueToGet = toGet.reduce((acc: any, field: any) => {
      const itemOptions = this.getOptionsConfig(field);
      const itemHash = this.createUniqueHash(itemOptions);
      if (
        !acc.some((i: any) => {
          const options = this.getOptionsConfig(i[1]);
          if (!options) {
            return false;
          }
          return this.createUniqueHash(options) === itemHash;
        })
      ) {
        return [...acc, [itemHash, field]];
      }
      return acc;
    }, []);
    return uniqueToGet;
  }

  public async getOptionsToDownload(
    schemas: any,
    key: string,
    accessors: string[],
  ): Promise<any> {
    const schema = schemas[key];
    if (!schema) {
      return {};
    }
    const uniqueOptionsToGet = this.filterUniqueOptions(schema, accessors);
    const res = await Promise.all(
      uniqueOptionsToGet.map(async (item: any) => {
        const options = this.getOptionsConfig(item[1]);
        const { results, pkey } = await this.requestService.get(
          "options",
          {
            formID: options.formID,
            optionFilters: options.requestArgs || {},
            output: options.out,
          },
          false,
        );
        return [item[0], this.compileOptions(results, options.out, pkey)];
      }),
    );
    return {
      [key]: this.mergeOptions(schema, Object.fromEntries(res), accessors),
    };
  }

  public async downloadFormOptions(schemas: any): Promise<any> {
    return {
      ...(await this.getOptionsToDownload(schemas, "schCFG_IREF_Type", [
        "generalForm.form",
        "vioInput.form",
      ])),
      ...(await this.getOptionsToDownload(schemas, "schCFG_IREF_Tabs", [
        "tabData.form",
      ])),
    };
  }

  public compileOptions(options: any, output: string, pkey: string): any {
    return options.map((option: any) => {
      return { value: option[pkey], text: compile(output, option) };
    });
  }

  public async downloadClient(
    auth: AuthCredentials,
    client: Client,
    loadingMessage: WritableSignal<string>,
    fresh: boolean = false,
  ): Promise<DownloadedClient> {
    this.objectURLService.clear();
    const clientConfig = await this.downloadClientConfig(loadingMessage);
    const schemas = await this.downloadSchemas(loadingMessage, fresh);
    const formOptions = await this.downloadFormOptions(schemas);
    const images = await this.downloadPrintables(loadingMessage, fresh);
    const dlClientID = `${auth.env}.${auth.userID}.${client.clientID}`;
    return this.saveDownloadedClient({
      dlClientID,
      env: auth.env,
      ...client,
      clientData: {
        syncHistory: {
          upload: {} as SyncHistoryItem,
          download: {
            syncID: uuid(),
            dts: new Date().getTime(),
          },
        },
        clientConfig,
        schemas: { ...schemas, ...formOptions },
        images,
        locations: {},
      },
    });
  }

  public async downloadPrintables(
    loadingMessage: WritableSignal<string>,
    fresh: boolean = false,
  ): Promise<any> {
    loadingMessage.set("Downloading Images...");
    const images = await this.getPrintablesToDownload(fresh);
    return Promise.all(
      Object.entries(images).map(async ([img, exists]) => {
        if (exists) {
          return {
            fileName: img,
            folder: `/${Directory.Data}`,
            path: `/${Directory.Data}/${img}`,
          };
        }
        const downloadRes = await this.requestService.downloadFile(
          `hscloudsuite-printables/${img}`,
          img,
          Directory.Data,
        );
        return { fileName: img, ...downloadRes };
      }),
    );
  }

  public async getPrintablesToDownload(fresh: boolean = false) {
    const res = await this.requestService.get("images", { fresh }, false);
    if (!res?.images) {
      throw new Error("Error Downloading Printables List");
    }
    return this.fileService.checkIfFilesExist(res.images, Directory.Data);
  }

  public async downloadClients(): Promise<Client[]> {
    const res: any = await this.requestService.get("clients");
    await this.setAvailableClients(res.data);
    return res.data;
  }

  private async downloadClientConfig(
    loadingMessage: WritableSignal<string>,
  ): Promise<ClientSettings> {
    loadingMessage.set("Downloading Client Settings...");
    const result = await this.requestService.get("clients/config", {}, false);
    if (!result?.system) {
      throw new Error("Failed To Download Client Config");
    }
    const { defaults, system, ...res } = result;
    const app = Object.keys(res).reduce(
      (acc: any, key: string) => {
        return {
          ...acc,
          [key]: safeParse(res[key]),
        };
      },
      { ...defaults },
    );

    // Now go through each system specific setting and set the value appropriately
    const systemSettings = system.DATA.reduce(
      (acc: any, { setting, settingValue }: any) => {
        return { ...acc, [setting]: safeParse(settingValue, settingValue) };
      },
      {},
    );

    return { ...app, ...systemSettings };
  }

  private async downloadSchemas(
    loadingMessage: WritableSignal<string>,
    fresh: boolean = false,
  ): Promise<any> {
    loadingMessage.set("Downloading Client Data...");
    const schemaListResponse: any = await this.requestService.get(
      "schemas",
      {
        fresh,
      },
      false,
    );
    if (!schemaListResponse?.schemas) {
      throw new Error("Failed To Download Schema List");
    }
    const schemaPromises = schemaListResponse.schemas
      .map(async (schema: any) => {
        const useScriptsWhere = schema.name === "HSScripts";
        const res = await this.requestService.get(
          `schemas/download`,
          {
            tableName: schema.name,
            where: useScriptsWhere
              ? JSON.stringify([
                  {
                    comparisonOperator: "LIKE",
                    logicalOperator: "AND",
                    value: "touch",
                    column: "runWhere",
                    type: "listguid",
                  },
                ])
              : "",
          },
          false,
        );
        return this.handleSchemas(res);
      })
      .filter((p: any) => p);
    const schemaArr = await Promise.all(schemaPromises);
    return schemaArr.reduce((acc: any, schema: any) => {
      return { ...acc, ...schema };
    }, {});
  }

  private getPrimaryKey(schema: any) {
    const pk = schema.keys.find((col: any) => col.type === "PK");
    return pk?.column;
  }

  private getForeignKey(schema: any) {
    const fk = schema.keys.find((col: any) => col.type === "FK");
    return fk?.column;
  }

  private parseInspectionTypes(records: InspectionType[]) {
    return records.map((record: any) => {
      const logMeta = {
        tableName: "schCFG_IREF_Type",
        inspectionType: record.type,
        inspectionTypeID: record.inspectionTypeID,
        record,
      };
      const advancedConfigs = safeJSONParse(
        record.advancedConfigs || "{}",
        {},
        this.logger,
        { ...logMeta, attemptingToParse: "advancedConfigs" },
      );
      const scoringDisplay = safeJSONParse(
        record.scoringDisplay || "[]",
        [] as any[],
        this.logger,
        { ...logMeta, attemptingToParse: "scoringDisplay" },
      );
      return {
        ...record,
        generalForm: safeJSONParse(
          record.generalForm,
          { form: [] },
          this.logger,
          { ...logMeta, attemptingToParse: "generalForm" },
        ),
        vioInput: safeJSONParse(record.vioInput, { form: [] }, this.logger, {
          ...logMeta,
          attemptingToParse: "vioInput",
        }),
        scoringConfig: safeJSONParse(
          record.scoringConfig || "{}",
          {},
          this.logger,
          { ...logMeta, attemptingToParse: "scoringConfig" },
        ),
        scoringDisplay,
        advancedConfigs: convertStringsToBooleans(advancedConfigs),
      };
    });
  }

  private parseTabs(records: Tab[]) {
    return records.map((record: any) => {
      const parsedTabData = safeJSONParse(
        record.tabData,
        { srcData: "" },
        this.logger,
        {
          tableName: "schCFG_IREF_Tabs",
          tabName: record.srcData,
          attemptingToParse: "tabData",
          inspectionTypeID: record.inspectionTypeID,
        },
      );
      return {
        ...record,
        srcData: record.srcData.replace("schTab", "Reload"),
        tabData: {
          ...parsedTabData,
        },
      };
    });
  }

  private parsePrintables(records: Printable[]) {
    return records.map((record: any) => {
      return {
        ...record,
        drawEngine: safeJSONParse(record.drawEngine, {}, this.logger),
      };
    });
  }

  private parseObservations(records: Printable[]) {
    return records.map((record: any) => {
      return {
        ...record,
        isViolation: safeJSONParse(record.isViolation, 0, this.logger),
      };
    });
  }

  public handleSchemas(res: any) {
    if (res.response === "Error" || res.success === false) {
      this.logger.error("Error downloading schema", res);
      return null;
    }
    const pk = this.getPrimaryKey(res.schema);
    const fk = this.getForeignKey(res.schema);
    if (res.schema.name === "schCFG_IREF_Tabs") {
      return {
        [res.schema.name]: {
          pk,
          fk,
          records: this.parseTabs(res.data.DATA),
        },
      };
    }
    if (res.schema.name === "schCFG_IREF_Printable") {
      return {
        [res.schema.name]: {
          pk,
          fk,
          records: this.parsePrintables(res.data.DATA),
        },
      };
    }
    if (res.schema.name === "schCFG_IREF_Type") {
      return {
        [res.schema.name]: {
          pk,
          fk,
          records: this.parseInspectionTypes(res.data.DATA),
        },
      };
    }
    if (res.schema.name === "schCFG_IREF_Item_Obs") {
      return {
        [res.schema.name]: {
          pk,
          fk,
          records: this.parseObservations(res.data.DATA),
        },
      };
    }
    return { [res.schema.name]: { pk, fk, records: res.data.DATA } };
  }

  public async setClientAuth(client: DownloadedClient) {
    await this.requestService.setClient(client.clientID);
    if (client.clientData?.clientConfig?.pages) {
      await this.requestService.setSearchVariables(
        client.clientData?.clientConfig?.pages,
      );
    }
  }

  public async setCurrentClient(
    client: DownloadedClient,
  ): Promise<DownloadedClient> {
    const hydratedInput = (await this.hydrateClient(
      client,
    )) as DownloadedClient;
    await this.setClientAuth(hydratedInput);
    const clientToPersist = await this.persistStaticClientData(hydratedInput);
    await this.downloadedClientRepo.saveClient({ ...clientToPersist });
    await this.downloadedClientRepo.setCurrentClientId(
      clientToPersist.dlClientID,
    );
    const hydratedClient = (await this.hydrateClient(
      clientToPersist,
    )) as DownloadedClient;
    this.currentClient.set(hydratedClient);
    this.setDefaultDateFormat(hydratedClient);
    await this.logService.updateMeta();
    if (!hydratedClient.clientData) {
      this.logger.error(
        new Error("setCurrentClient called with no clientData"),
      );
    }
    return hydratedClient;
  }

  public getCurrentClient(): DownloadedClient {
    return this.currentClient();
  }

  public isValidClient(client: DownloadedClient): boolean {
    return (
      !!client &&
      !!client.clientID &&
      !!client.clientData &&
      !!client.clientData.clientConfig &&
      !!client.clientData.schemas &&
      !!client.clientData.syncHistory
    );
  }

  public async getCurrentClientSafe(): Promise<DownloadedClient> {
    const currentClient = this.currentClient();
    if (this.isValidClient(currentClient)) {
      return currentClient;
    }
    const persistedCurrent = await this.downloadedClientRepo.getCurrentClient();
    const hydratedDbCurrent = (await this.hydrateClient(
      persistedCurrent || null,
    )) as DownloadedClient;
    if (!this.isValidClient(hydratedDbCurrent)) {
      this.logger.error("No valid current client found");
    }
    return hydratedDbCurrent;
  }

  public getCurrentClientConfig(): ClientSettings {
    return this.currentClient().clientData.clientConfig;
  }
}
